import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { elementAt, Subscription } from 'rxjs';

import { Buffer } from 'buffer';
import jwt_decode from 'jwt-decode';
import { Amplify, Auth } from 'aws-amplify';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';

import { amplifyconfig } from 'src/environments/authConfig';
import { IUserDetails } from 'src/app/core/models/userDetails.interface';
import Constants from '../../../core/constants/constants';

import { RestAPIsService } from '../../../core/services/rest-apis.service';
import { SessionService } from 'src/app/core/services/session.service';

Amplify.configure(amplifyconfig);
Auth.configure(amplifyconfig);

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  isLoading: boolean = false;
  isUserValid: boolean = true;
  subscriptions: Subscription[] = [];
  userDetails!: IUserDetails;

  loginForm: FormGroup;

  isSubmitted: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  siteId: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private restAPI: RestAPIsService,
    private modalService: NgbModal,
    private sessionService: SessionService,
    private spinner: NgxSpinnerService,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(Constants.Regex.Email)]]
    });
  }

  ngOnInit() {
    this.subscriptions.push(
      this.route.params.subscribe(async (params) => {
        if (!!params['signature']) {
          // store host
          const info = this.sessionService.getItemFromSession(Constants.CONFIG.INFO);
          // Clear session storage
          this.sessionService.clearSessionStorage();
          // Set the host and source
          this.sessionService.setItemIntoSession(Constants.CONFIG.INFO, info);
          this.isLoading = true;
          this.spinner.show();
          try {
            this.sessionService.setItemIntoSession(Constants.sessionConstants.SIGNATURE, params['signature']);
            const signature = Buffer.from(
              params['signature'],
              'base64'
            ).toString('binary');
            const decodedSign: any = jwt_decode(signature);
            // this.siteId = siteId;
            
            const { authChallenge, email } = decodedSign;
            this.siteId = this.route.snapshot.queryParams['siteId'] || '';
            console.log(decodedSign, this.route.snapshot.queryParams, this.siteId);
            const user = await Auth.signIn(email);
            const challengeResp = await Auth.sendCustomChallengeAnswer(user, authChallenge);
            this.sessionService.setItemIntoSession(Constants.sessionConstants.SESSION_UID, authChallenge);
            if(challengeResp?.signInUserSession){
              // Get user details
                this.userValidate(params['signature'], email);
            }
            else{
              this.spinner.hide();
              this.router.navigate(['/modules/page-not-found'], {
                queryParams: { content: 'The login link has expired. Please sign in again to proceed.' },
              });
            }
          } catch (err) {
            console.log('ERROR: ', err);
            this.router.navigate(['/modules/page-not-found'], {
              queryParams: { content: 'User not found.' },
            });
            this.spinner.hide();
          }
        }
      })
    );
  }

  userValidate(signature: string, email:string): void {
    this.restAPI.getAPI(`carbon/v3/user/validate/${encodeURIComponent(signature)}`, 'domestic').subscribe({
      next: (res) => {
        if(res.code == Constants.HTTP_STATUS.OK){
          this.getUserDetails(email);
        }
        else{
          this.spinner.hide();
          this.router.navigate(['/modules/page-not-found'], {
            queryParams: { content: 'The login link has expired. Please sign in again to proceed.' },
          });
        }
      },
      error: (err) => {
        this.spinner.hide();
        this.router.navigate(['/modules/page-not-found'], {
          queryParams: { content: 'The login link has expired. Please sign in again to proceed.' },
        });
      },
    })
  }

  getUserDetails(email: string): void {
    this.restAPI.getAPI(`carbon/v3/user?${new URLSearchParams({ email: email }).toString()}`, 'domestic').subscribe({
      next: (user) => {
        this.userDetails = user?.data;

        if(this.userDetails.accountType === 'BROKER'){
          // Store into memory
          this.sessionService.setItemIntoSession(Constants.sessionConstants.EMAIL, email);
          this.sessionService.setItemIntoSession(Constants.sessionConstants.USERDETAILS, this.userDetails);

          // Get company details
          this.getCompanyDetails();
        }
        else{
          this.spinner.hide();
          this.router.navigate(['/modules/page-not-found'], {
            queryParams: { content: 'User not found.' },
          });
          this.isUserValid = false;
        }  

      }, error: (err) => {
        this.spinner.hide();
        console.log('ERROR: ', err);
        this.isUserValid = false;
        this.router.navigate(['/modules/page-not-found'], {
          queryParams: { content: 'User not found.' },
        });
      }
    })
  }

  // Get company details
  getCompanyDetails(): void {
    this.subscriptions.push(
      this.restAPI.getAPI(`carbon/v3/get-site?${new URLSearchParams({ customerEmail: this.userDetails.email }).toString()}`, 'domestic').subscribe({
        next: (company) => {
          const companyDetails = company?.data?.companyResponse;
          this.spinner.hide();
          // For Broker
          if (this.userDetails?.accountType === 'BROKER' || this.userDetails?.accountType == null) {
            // Company details length is greater than 0 th
            if (companyDetails?.length > 0) {
              this.sessionService.setItemIntoSession(Constants.sessionConstants.ISVERIFYEMAIL, true);
              if(this.siteId){
                const selectedSite = companyDetails.find((site: any) => site.siteId === this.siteId);
                if(selectedSite){
                  this.sessionService.setItemIntoSession(Constants.sessionConstants.SELECTEDSITE, selectedSite?.siteId);
                  this.sessionService.setItemIntoSession(Constants.sessionConstants.COMPANY_SITE_DATA, selectedSite);
                  this.router.navigate(['/modules/dashboard']);
                }
                else{
                  this.router.navigate(['/page-not-found'], {
                    queryParams: { content: 'Site not found' },
                  });
                }
              }
              else{
                // selected site from the list of sites, top one
                this.sessionService.setItemIntoSession(Constants.sessionConstants.SELECTEDSITE, companyDetails[0]?.siteId);
                this.sessionService.setItemIntoSession(Constants.sessionConstants.COMPANY_SITE_DATA, companyDetails[0]);
                // is verify email set is true
                // Navigate to dashboard
                this.router.navigate(['/modules/dashboard']);
              }
            } else {
              // no company details then navigate to add company
              this.router.navigate(['modules/companies/add-company']);
            }
            this.restAPI.addCheckpoint({ event: 'LOGIN', source: null });
          } else {
            this.spinner.hide();
            this.router.navigate(['/modules/page-not-found'], {
              queryParams: { content: 'Access denied. Your account does not have the necessary permissions.' },
            });
          }
        },error: (err) => {
          this.spinner.hide();
          console.log('ERROR: ', err);
          if(err?.error?.code == 404){
            this.router.navigate(['/modules/companies/add-company'])
          }
          else{
            this.router.navigate(['/modules/page-not-found'], {
              queryParams: { content: 'Company not found.' },
            });
          }
        }
      })
    );
  }

  // On enter
  onEnter(key: KeyboardEvent): void {
    if (key?.key === 'Enter') {
      this.onSubmit();
    }
  }

  // Close modal
  close(): void {
    this.modalService.dismissAll();
  }

  // Login
  onSubmit(): void {
    this.successMessage = '';
    this.errorMessage = '';
    if (!this.isSubmitted && this.loginForm.valid) {
      this.isSubmitted = true;
      const url = this.sessionService.getItemFromSession(Constants.CONFIG.INFO)?.origin || window.location.origin;
      const source = this.sessionService.getItemFromSession(Constants.CONFIG.INFO)?.source;

      const payload = {
        email: this.loginForm.controls['email'].value?.toLowerCase(),
        subject: (source === 'localhost') ? '' : ((source === 'consultivutilities') ? 'Consultiv Utilities' : (source === 'advantageutilities') ? 'Advantage Utilities' : source.toUpperCase()) + ' Low Carbon Recommendation Login',
        URL: url + '/login',
      };
      this.subscriptions.push(
        this.restAPI
          .postAPI('carbon/v2/user-login', payload, 'domestic')
          .subscribe({
            next: (res) => {
              this.successMessage = res.message;
              this.isSubmitted = false;
            },
            error: (err) => {
              this.isSubmitted = false;
              if (!!err?.error) {
                this.errorMessage = err.error.error;
              }
            },
          })
      );
    } else {
      this.loginForm.markAllAsTouched();
    }
  }
}